// Fetch wrapper for the StoreFront SPA — Nexus gateway-native auth.
//
//  • JWT (access) + refresh token in sessionStorage.
//  • callFn(action, payload) → POST /v1/app/storefront/fn/storefront_api with
//    {action, payload}; unwraps the dispatcher { success, message, data } envelope.
//  • callPublic(action, payload) → storefront_public (requiresUser:false) — no
//    Bearer; the edge stamps the app key.
//  • Email/password sign-in and registration against /auth/* with appId=storefront
//    (same accounts as the iOS app).

import { APP_ID, GATEWAY_BASE, appFnUrl } from './config';

// Sessions are PER STORE: an account belongs to one store, so signing in at
// Vinny's must not make Goombas look signed in. Tokens are keyed by the
// store slug in the URL (/:slug/...); the directory has no session.
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export function currentSlug(): string {
  try {
    const seg = window.location.pathname.split('/')[1] || '';
    return SLUG_RE.test(seg) ? seg : '';
  } catch { return ''; }
}
const TOKEN_KEY = () => `sf.jwt.${currentSlug()}`;
const REFRESH_KEY = () => `sf.refresh.${currentSlug()}`;
const KIND_KEY = () => `sf.kind.${currentSlug()}`;
// One stable id per browser so a guest's sessions can be tied together.
const DEVICE_KEY = 'sf.device';

export type SessionKind = 'guest' | 'member';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// localStorage (not sessionStorage): a guest who closes the tab must still be
// able to open the store and see the order they just placed.
function store(): Storage | null {
  try { return window.localStorage; } catch { try { return window.sessionStorage; } catch { return null; } }
}
export function getToken(): string | null { return currentSlug() ? store()?.getItem(TOKEN_KEY()) ?? null : null; }
export function setToken(t: string | null): void {
  const s = store(); if (!s || !currentSlug()) return;
  if (t) s.setItem(TOKEN_KEY(), t);
  else { s.removeItem(TOKEN_KEY()); s.removeItem(REFRESH_KEY()); s.removeItem(KIND_KEY()); }
}
function getRefresh(): string | null { return currentSlug() ? store()?.getItem(REFRESH_KEY()) ?? null : null; }
function setRefresh(t: string | null): void {
  const s = store(); if (!s || !currentSlug()) return;
  if (t) s.setItem(REFRESH_KEY(), t); else s.removeItem(REFRESH_KEY());
}
export function isAuthed(): boolean { return !!getToken(); }
export function getRefreshToken(): string | null { return getRefresh(); }
export function sessionKind(): SessionKind | null {
  if (!getToken()) return null;
  return (store()?.getItem(KIND_KEY()) as SessionKind | null) || 'member';
}
function setKind(k: SessionKind | null): void {
  const s = store(); if (!s || !currentSlug()) return;
  if (k) s.setItem(KIND_KEY(), k); else s.removeItem(KIND_KEY());
}
export function isGuest(): boolean { return sessionKind() === 'guest'; }
function deviceId(): string {
  try {
    const ls = window.localStorage;
    let id = ls.getItem(DEVICE_KEY);
    if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`); ls.setItem(DEVICE_KEY, id); }
    return id;
  } catch { return 'unknown'; }
}

// ---- silent refresh (single-flight) ----
let refreshInFlight: Promise<boolean> | null = null;
async function doRefresh(): Promise<boolean> {
  const rt = getRefresh();
  if (!rt) return false;
  try {
    const res = await window.fetch(`${GATEWAY_BASE}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) { setToken(null); return false; }
    const data = (await res.json().catch(() => null)) as { accessToken?: string; refreshToken?: string } | null;
    if (!data?.accessToken) { setToken(null); return false; }
    setToken(data.accessToken);
    if (data.refreshToken) setRefresh(data.refreshToken);
    return true;
  } catch { return false; }
}
function refreshAccess(): Promise<boolean> {
  if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try { return JSON.parse(text) as T; }
  catch { throw new ApiError(res.status, `Non-JSON response: ${text.slice(0, 160)}`); }
}

interface Envelope<T> { success?: boolean; message?: string; data?: T; code?: string; }
function unwrap<T>(env: Envelope<T> | T): T {
  if (env && typeof env === 'object' && 'data' in (env as object)) {
    const e = env as Envelope<T>;
    if (e.success === false) throw new ApiError(400, e.message || 'Request failed', e);
    return (e.data as T);
  }
  return env as T;
}

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Call the signed-in app API with one retry across a silent token refresh. */
export async function callFn<T = unknown>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const send = async (): Promise<Response> => window.fetch(appFnUrl('storefront_api'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ action, payload }),
  });

  let res = await send();
  if (res.status === 401 && getRefresh()) {
    if (await refreshAccess()) res = await send();
  }
  if (res.status === 401) { setToken(null); throw new ApiError(401, 'Your session has expired. Please sign in again.'); }
  if (!res.ok) {
    const body = await readJson<Envelope<unknown>>(res).catch(() => undefined);
    throw new ApiError(res.status, body?.message || `HTTP ${res.status}`, body);
  }
  return unwrap<T>(await readJson<Envelope<T>>(res));
}

/** Call the public (no login) store API. */
export async function callPublic<T = unknown>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const res = await window.fetch(appFnUrl('storefront_public'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, payload }),
  });
  if (!res.ok) {
    const body = await readJson<Envelope<unknown>>(res).catch(() => undefined);
    throw new ApiError(res.status, body?.message || `HTTP ${res.status}`, body);
  }
  return unwrap<T>(await readJson<Envelope<T>>(res));
}

// ---- email / password ----
export async function loginPassword(email: string, password: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  const body = await readJson<{ accessToken?: string; refreshToken?: string; message?: string; code?: string }>(res);
  if (body?.code === 'PASSWORD_CHANGE_REQUIRED') {
    throw new ApiError(res.status, 'This account must set a new password before its first sign-in. Use “Forgot password” to set one.');
  }
  if (!res.ok || !body?.accessToken) throw new ApiError(res.status, body?.message || 'Sign-in failed');
  setToken(body.accessToken);
  if (body.refreshToken) setRefresh(body.refreshToken);
  setKind('member');
}

export function logout(): void { setToken(null); }

// ---- guest checkout (PLAN_GUEST_CHECKOUT G-P3) ----
//
// A guest is a real Nexus user (kind='guest') minted by POST /auth/guest; the
// edge stamps the app key. Tokens live in the same per-store slots, so every
// call works unchanged. The guest can later become an account in place
// (upgradeGuest) or be merged into an existing one (linkGuest).

/** Mint a guest session for this store. No-op when a session already exists. */
export async function startGuest(): Promise<void> {
  if (isAuthed()) return;
  const res = await window.fetch(`${GATEWAY_BASE}/auth/guest`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appId: APP_ID, deviceId: deviceId() }),
  });
  const body = await readJson<{ accessToken?: string; refreshToken?: string; message?: string; code?: string }>(res).catch(() => null);
  if (!res.ok || !body?.accessToken) {
    throw new ApiError(res.status, body?.code === 'CAPABILITY_DENIED' ? 'Guest checkout is not available here yet.' : body?.message || 'Could not start a guest session.');
  }
  setToken(body.accessToken);
  if (body.refreshToken) setRefresh(body.refreshToken);
  setKind('guest');
}

/** The signed-in identity from the platform (name/email/phone prefill). */
export interface AccountMe { userId: string; email: string; emailVerified: boolean; givenName: string | null; familyName: string | null; phone: string | null; }
export async function fetchAccount(): Promise<AccountMe | null> {
  if (!isAuthed()) return null;
  const send = () => window.fetch(`${GATEWAY_BASE}/auth/me`, { headers: authHeaders() });
  let res = await send();
  if (res.status === 401 && getRefresh()) { if (await refreshAccess()) res = await send(); }
  if (!res.ok) return null;
  const b = await readJson<AccountMe>(res).catch(() => null);
  return b && b.userId ? b : null;
}

/**
 * Turn the current guest into an account (same user id — the orders stay).
 * The platform emails a 6-digit code; finish with verifyEmail + loginPassword.
 * Throws ApiError with code EMAIL_EXISTS when the email already has an account.
 */
export async function upgradeGuest(email: string, password: string, name?: string): Promise<void> {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const givenName = parts[0]; const familyName = parts.slice(1).join(' ') || undefined;
  const send = () => window.fetch(`${GATEWAY_BASE}/auth/guest/upgrade`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ email, password, ...(givenName ? { givenName } : {}), ...(familyName ? { familyName } : {}) }),
  });
  let res = await send();
  if (res.status === 401 && getRefresh()) { if (await refreshAccess()) res = await send(); }
  const body = await readJson<{ code?: string; message?: string; errors?: string[] }>(res).catch(() => ({} as { code?: string; message?: string; errors?: string[] }));
  if (res.ok) return;
  if (body?.code === 'EMAIL_EXISTS') throw new ApiError(409, 'This email already has an account — sign in to add this order to it.', body);
  if (body?.code === 'WEAK_PASSWORD') throw new ApiError(400, (body.errors || ['Password does not meet the policy.']).join(' '), body);
  throw new ApiError(res.status, body?.message || 'Could not create the account.', body);
}

/** After signing in to an ACCOUNT, merge the previous guest (by its refresh token) into it. */
export async function linkGuest(guestRefreshToken: string): Promise<boolean> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/guest/link`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ guestRefreshToken, appId: APP_ID }),
  });
  const body = await readJson<{ linked?: boolean }>(res).catch(() => null);
  return res.ok && !!body?.linked;
}

/** Register a password account. Emails a 6-digit code. */
export async function register(email: string, password: string, displayName?: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/register`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID, ...(displayName ? { displayName } : {}) }),
  });
  const body: { code?: string; message?: string } = await readJson<{ code?: string; message?: string }>(res).catch(() => ({}));
  if (res.ok) return;
  if (body?.code === 'PENDING_VERIFICATION') return;
  if (body?.code === 'EMAIL_EXISTS') throw new ApiError(409, 'This email already has an account — sign in instead.');
  throw new ApiError(res.status, body?.message || 'Registration failed');
}

export async function verifyEmail(email: string, code: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/verify-email`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  const body: { message?: string } = await readJson<{ message?: string }>(res).catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body?.message || 'Could not verify that code.');
}

export async function resendVerification(email: string): Promise<void> {
  await window.fetch(`${GATEWAY_BASE}/auth/resend-verification`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await window.fetch(`${GATEWAY_BASE}/auth/forgot-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/reset-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  const body: { message?: string } = await readJson<{ message?: string }>(res).catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body?.message || 'Could not reset password.');
}

// ---- file upload via the platform File Service (owner media uploads) ----
export interface UploadResult { url: string; filename: string; contentType: string; sizeBytes?: number; }
export async function uploadFile(file: File): Promise<UploadResult> {
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
  const safeName = (file.name || 'image').replace(/[^\x20-\x7E]/g, '_');
  const send = () => window.fetch(`${GATEWAY_BASE}/v1/app/${APP_ID}/files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ filename: safeName, contentType: file.type || 'application/octet-stream', dataBase64 }),
  });
  let res = await send();
  if (res.status === 401 && getRefresh()) { if (await refreshAccess()) res = await send(); }
  const body = await readJson<{ url?: string; sizeBytes?: number; message?: string; error?: string }>(res);
  if (!res.ok || !body?.url) throw new ApiError(res.status, body?.message || body?.error || 'Upload failed');
  return { url: body.url, filename: safeName, contentType: file.type || 'application/octet-stream', sizeBytes: body.sizeBytes };
}

// ---- chat: POST /v1/chat (SSE, always stream:true) ----
export interface ChatWireMessage { role: 'system' | 'user' | 'assistant'; content: string; }

/**
 * Stream one chat turn. Calls onChunk for each text delta; resolves when the
 * gateway sends [DONE] or closes the stream. Never sends stream:false.
 */
export async function streamChat(
  messages: ChatWireMessage[],
  onChunk: (text: string) => void,
  signal?: AbortSignal,
  conversationId?: string,
): Promise<void> {
  const body = {
    requestId: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())),
    appId: APP_ID,
    // provider/model are omitted (not null): the gateway rejects null and
    // picks the instance default, exactly as the iOS app does.
    messages,
    stream: true,
    maxTokens: 1024,
    // Stable per-chat-thread handle: the gateway keeps ONE persistent
    // claude session per (app, user, conversation) so warm turns skip
    // the CLI cold start (PLAN_AI_CHAT_PERSISTENT_SESSIONS.md).
    ...(conversationId ? { conversationId } : {}),
  };
  const send = () => window.fetch(`${GATEWAY_BASE}/v1/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders() },
    body: JSON.stringify(body),
    signal,
  });
  let res = await send();
  if (res.status === 401 && getRefresh()) { if (await refreshAccess()) res = await send(); }
  if (!res.ok || !res.body) {
    const b = await readJson<Envelope<unknown>>(res).catch(() => undefined);
    throw new ApiError(res.status, b?.message || `Chat failed (HTTP ${res.status})`, b);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).replace(/\r$/, '');
      buf = buf.slice(nl + 1);
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') return;
      try {
        const parsed = JSON.parse(data) as { choices?: { delta?: { content?: string } }[]; error?: string | { message?: string } };
        // In-stream error frame: the gateway sends `{"error": "<message>"}` once
        // SSE headers are out (e.g. the AI host is offline).
        const errMsg = typeof parsed.error === 'string' ? parsed.error : parsed.error?.message;
        if (errMsg) throw new ApiError(500, errMsg);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onChunk(content);
      } catch (e) {
        if (e instanceof ApiError) throw e;
        // malformed line — skip
      }
    }
  }
}
